import { api, rp, dt } from "@/lib/api";
import { Table, useFetch, Spinner, StatCard, EmptyState } from "@/components/shared";
import { AlertTriangle } from "lucide-react";

const RankList = ({ title, rows, testId, fmt }) => (
  <div className="panel p-3" data-testid={testId}>
    <div className="eyebrow mb-2">{title}</div>
    {rows.length === 0 ? <div className="text-xs text-zinc-600">-</div> : rows.map((r, i) => (
      <div key={r.name} className="flex items-center gap-2 py-1 text-sm border-b border-zinc-800/60 last:border-0">
        <span className="text-[10px] font-mono text-zinc-600 w-4">{i + 1}</span>
        <span className="flex-1 truncate">{r.name}</span>
        <span className={`font-mono text-xs ${i === 0 && r.count > 1 ? "text-amber-400 font-bold" : "text-zinc-400"}`}>{fmt ? fmt(r) : `${r.count}×`}</span>
      </div>))}
  </div>
);

export default function CashierCorrections({ range }) {
  const { data, loading } = useFetch(() => api.get(`/reports/cashier-corrections?start=${range.start}&end=${range.end}`).then((r) => r.data), [range]);
  if (loading || !data) return <Spinner />;
  const max = Math.max(1, ...data.by_day.map((d) => d.count));
  return (
    <div className="mt-8" data-testid="cashier-corrections">
      <div className="flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4 text-amber-400" /><div className="eyebrow">Koreksi kasir (human error)</div></div>
      <p className="text-xs text-zinc-500 mb-3">Item yang ditambahkan/dihapus kasir di invoice WO karena terlewat. Pola berulang = perlu dibenahi di alur mekanik/gudang.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard label="Koreksi tambah" value={data.count_add} accent={data.count_add > 0} testId="corr-add" />
        <StatCard label="Koreksi hapus" value={data.count_remove} testId="corr-remove" />
        <StatCard label="Nilai item terlewat" value={rp(data.value_added)} testId="corr-value" />
        <StatCard label="WO terkoreksi" value={`${data.rate_pct}%`} testId="corr-rate" />
      </div>
      {data.rows.length === 0 ? <EmptyState text="Tidak ada koreksi kasir pada rentang ini — bagus!" /> : (<>
        <div className="panel p-3 mb-4" data-testid="corr-by-day">
          <div className="eyebrow mb-2">Per hari</div>
          <div className="flex items-end gap-1 h-20">
            {data.by_day.map((d) => <div key={d.day} className="flex-1 max-w-16 flex flex-col items-center gap-1" title={`${d.day}: ${d.count} koreksi`}>
              <span className="text-[10px] font-mono text-zinc-400">{d.count}</span>
              <div className="w-full bg-amber-600/80 rounded-t" style={{ height: `${(d.count / max) * 56}px` }} />
              <span className="text-[9px] text-zinc-600 font-mono">{d.day.slice(5)}</span>
            </div>)}
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <RankList title="Kasir paling sering koreksi" rows={data.by_cashier} testId="corr-by-cashier" />
          <RankList title="Mekanik WO terkoreksi" rows={data.by_mechanic} testId="corr-by-mechanic" />
          <RankList title="Alasan berulang" rows={data.by_reason} testId="corr-by-reason" />
          <RankList title="Item sering terlewat" rows={data.by_item} testId="corr-by-item" fmt={(r) => `${r.count}× · ${rp(r.amount)}`} />
        </div>
        <Table testId="corr-table" cols={["Waktu", "Aksi", "Kasir", "WO", "Mekanik", "Item", "Qty", "Nilai", "Alasan"]} rows={data.rows} keyFn={(r) => r.log_id} empty="-"
          render={(r) => (<>
            <td className="text-xs text-zinc-500 whitespace-nowrap">{dt(r.at)}</td>
            <td><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.action === "TAMBAH" ? "bg-amber-900/60 text-amber-300" : "bg-zinc-800 text-zinc-300"}`}>{r.action}</span></td>
            <td className="text-xs">{r.cashier}</td>
            <td className="font-mono text-xs">{r.wo_number}<div className="text-zinc-500">{r.plate}</div></td>
            <td className="text-xs text-zinc-400">{r.mechanics.join(", ") || "-"}</td>
            <td className="text-sm">{r.name}<span className="ml-1 text-[10px] text-zinc-500">{r.kind === "SERVICE" ? "jasa" : "part"}</span></td>
            <td className="font-mono">{r.qty}</td>
            <td className="font-mono">{rp(r.amount)}</td>
            <td className="text-xs text-zinc-300">{r.reason || "-"}</td>
          </>)} />
      </>)}
    </div>
  );
}
